import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { onSessionExpired } from "../api/client";
import { fetchCurrentUser, login as loginRequest, logout as logoutRequest } from "../api/auth";
import { AuthContext } from "./AuthContext";

/**
 * Holds the signed-in user for the whole app.
 *
 * There is no client-side token: the browser holds Django's session cookie, so
 * "am I signed in?" is answered by asking the API on start-up. A session can
 * also end while the app is open, which the API client reports here.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading");
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    let active = true;

    fetchCurrentUser()
      .then((currentUser) => {
        if (active) {
          setUser(currentUser);
          setStatus("authenticated");
        }
      })
      .catch(() => {
        if (active) {
          setUser(null);
          setStatus("anonymous");
        }
      });

    return () => {
      active = false;
    };
  }, []);

  // Tracked in a ref as well as state so the subscriber below reads the current
  // value without being re-registered on every change.
  const isAuthenticatedRef = useRef(false);

  useEffect(() => {
    isAuthenticatedRef.current = status === "authenticated";
  }, [status]);

  useEffect(
    () =>
      onSessionExpired(() => {
        // Only a session that was live can expire. Ignoring the rest keeps the
        // start-up check and the login page from being treated as an expiry.
        if (!isAuthenticatedRef.current) {
          return;
        }
        // Set immediately: several in-flight requests can fail together, and
        // this should count as one expiry.
        isAuthenticatedRef.current = false;

        setUser(null);
        setStatus("anonymous");
        setSessionExpired(true);
      }),
    [],
  );

  const signIn = useCallback(async (credentials) => {
    const signedInUser = await loginRequest(credentials);
    setUser(signedInUser);
    setStatus("authenticated");
    setSessionExpired(false);
    return signedInUser;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      // Drop local state even if the request failed; the session may already
      // be gone server-side, and staying "signed in" would be misleading.
      setUser(null);
      setStatus("anonymous");
      // Leaving on purpose is not an expiry, so no notice on the login page.
      setSessionExpired(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      status,
      isLoading: status === "loading",
      isAuthenticated: status === "authenticated",
      sessionExpired,
      hasRole: (...roles) => Boolean(user && roles.includes(user.role)),
      permissions: user?.permissions || [],
      signIn,
      signOut,
      setUser,
    }),
    [user, status, sessionExpired, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
